import {
  Component,
  Injectable,
  OnChanges,
  Input,
  OnInit,
  SimpleChanges,
  ChangeDetectorRef,
  ViewChild,
  inject,
  output,
} from '@angular/core';
import {
  NgbDate,
  NgbDateStruct,
  NgbDatepicker,
  NgbDatepickerI18n,
} from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Schedule } from 'src/app/models/schedule';

const SCHEDULE_I18N_VALUES = {
  es: {
    weekdays: ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'],
    weekdayFulls: [
      'Lunes',
      'Martes',
      'Miércoles',
      'Jueves',
      'Viernes',
      'Sábado',
      'Domingo',
    ],
    months: [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre',
    ],
  },
} as const;

@Injectable()
class ScheduleDatepickerI18n extends NgbDatepickerI18n {
  private readonly lang = 'es' as const;

  getWeekdayShortName(weekday: number): string {
    return SCHEDULE_I18N_VALUES[this.lang].weekdays[weekday - 1];
  }

  getWeekdayLabel(weekday: number): string {
    return SCHEDULE_I18N_VALUES[this.lang].weekdays[weekday - 1];
  }

  getWeekdayFullName(weekday: number): string {
    return SCHEDULE_I18N_VALUES[this.lang].weekdayFulls[weekday - 1];
  }

  getMonthShortName(month: number): string {
    return SCHEDULE_I18N_VALUES[this.lang].months[month - 1];
  }

  getMonthFullName(month: number): string {
    return this.getMonthShortName(month);
  }

  getDayAriaLabel(date: NgbDateStruct): string {
    return `${date.day}-${date.month}-${date.year}`;
  }
}

@Component({
  selector: 'app-schedule-datepicker',
  templateUrl: './schedule-datepicker.component.html',
  styleUrls: ['./schedule-datepicker.component.css'],
  providers: [{ provide: NgbDatepickerI18n, useClass: ScheduleDatepickerI18n }],
})
export class ScheduleDatepickerComponent implements OnInit, OnChanges {
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() minimumLeadDays: number | null = null;
  @Input() minimumDateIso: string | null = null;
  @Input() showSchedules = true;
  @Input() initialSchedule: Schedule | null = null;

  @ViewChild(NgbDatepicker) datepicker?: NgbDatepicker;

  readonly validChange = output<boolean>();

  form!: FormGroup;
  schedules?: unknown;
  datepickerVisible = true;
  selectedDateLabel = '';
  selectedDateModel: NgbDateStruct | null = null;
  private selectedDateIso = '';
  private readonly maxSelectableDays = 30;

  ngOnInit(): void {
    this.initForm();
    queueMicrotask(() => this.applyRestrictionWindow());
    queueMicrotask(() => this.applyInitialSchedule());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['minimumLeadDays'] || changes['minimumDateIso']) && this.datepicker) {
      this.applyRestrictionWindow();
    }
    if (changes['initialSchedule'] && this.form) {
      this.applyInitialSchedule();
    }
    if (changes['showSchedules'] && this.form) {
      this.syncScheduleControlValidators();
      if (!this.showSchedules) {
        this.schedules = undefined;
        this.form.get('schedule')?.setValue('');
      }
      this.emitValidity();
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      schedule: [''],
    });
    this.syncScheduleControlValidators();
    this.form.statusChanges.subscribe(() => {
      this.emitValidity();
    });
    this.emitValidity();
  }

  private syncScheduleControlValidators(): void {
    const scheduleControl = this.form.get('schedule');
    if (!scheduleControl) {
      return;
    }
    scheduleControl.setValidators(this.showSchedules ? [Validators.required] : []);
    scheduleControl.updateValueAndValidity({ emitEvent: false });
  }

  private emitValidity(): void {
    this.validChange.emit(this.showSchedules ? this.form.valid : !!this.selectedDateIso);
  }

  private applyInitialSchedule(): void {
    const schedule = this.initialSchedule;
    if (!schedule?.date || this.selectedDateIso === schedule.date) {
      return;
    }
    const parts = schedule.date.split('-').map(Number);
    if (parts.length !== 3 || parts.some((value) => Number.isNaN(value))) {
      return;
    }
    const [year, month, day] = parts;
    this.selectedDateModel = { year, month, day };
    const jsDate = new Date(year, month - 1, day);
    const weekdayIndex = (jsDate.getDay() + 6) % 7;
    const monthName = SCHEDULE_I18N_VALUES.es.months[month - 1];
    this.selectedDateLabel = `${SCHEDULE_I18N_VALUES.es.weekdayFulls[weekdayIndex]}, ${day} de ${monthName} de ${year}`;
    this.selectedDateIso = schedule.date;
    if (this.showSchedules && schedule.id) {
      this.form.get('schedule')?.setValue(JSON.stringify(schedule), { emitEvent: false });
    }
    this.emitValidity();
  }

  private applyRestrictionWindow(): void {
    if (!this.datepicker) {
      return;
    }
    const minimumDateByIso = this.parseIsoDate(this.minimumDateIso);
    const minDate = minimumDateByIso ?? this.resolveMinimumDateByLeadDays();
    const maxDate = this.addDays(minDate, this.maxSelectableDays);
    this.datepicker.minDate = this.toNgbDateStruct(minDate);
    this.datepicker.maxDate = this.toNgbDateStruct(maxDate);
  }

  private resolveMinimumDateByLeadDays(): Date {
    const today = new Date();
    const minimumLeadDays = Math.max(this.minimumLeadDays ?? 0, 0);
    return this.addDays(today, minimumLeadDays);
  }

  private parseIsoDate(value: string | null): Date | null {
    if (!value) {
      return null;
    }
    const parts = value.split('-').map(Number);
    if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
      return null;
    }
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }

  private addDays(baseDate: Date, days: number): Date {
    const result = new Date(baseDate);
    result.setDate(result.getDate() + days);
    return result;
  }

  private toNgbDateStruct(date: Date): NgbDateStruct {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  isDisabled = (date: NgbDateStruct, current?: { month: number }): boolean => {
    if (!current) {
      return false;
    }
    const jsDate = new Date(date.year, date.month - 1, date.day);
    const dayOfWeek = jsDate.getDay();
    if(date.year == 2026 && date.month == 6 && date.day == 29 ||
      date.year == 2026 && date.month == 7 && date.day == 16)return true;
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  selectDate(date: NgbDate): void {
    this.selectedDateModel = {
      year: date.year,
      month: date.month,
      day: date.day,
    };
    const jsDate = new Date(date.year, date.month - 1, date.day);
    const weekdayIndex = (jsDate.getDay() + 6) % 7;
    const monthName = SCHEDULE_I18N_VALUES.es.months[date.month - 1];
    this.selectedDateLabel = `${SCHEDULE_I18N_VALUES.es.weekdayFulls[weekdayIndex]}, ${date.day} de ${monthName} de ${date.year}`;
    this.selectedDateIso = `${date.year}-${date.month}-${date.day}`;

    if (!this.showSchedules) {
      this.schedules = undefined;
      this.form.get('schedule')?.setValue('');
      this.emitValidity();
    }
  }

  resetSelection(): void {
    this.selectedDateLabel = '';
    this.selectedDateModel = null;
    this.selectedDateIso = '';
    this.schedules = undefined;
    this.form.reset({ schedule: '' }, { emitEvent: false });
    this.syncScheduleControlValidators();
    this.datepickerVisible = false;
    this.cdr.detectChanges();
    this.datepickerVisible = true;
    this.cdr.detectChanges();
    this.applyRestrictionWindow();
    this.emitValidity();
  }

  getSelection(): { schedule: Schedule } | null {
    if (!this.selectedDateIso) {
      return null;
    }
    if (!this.showSchedules) {
      return {
        schedule: {
          id: 0,
          date: this.selectedDateIso,
          section: '',
          schedule_range: '',
          price: '0',
        },
      };
    }
    if (!this.form.valid) {
      return null;
    }
    const raw = this.form.get('schedule')?.value as string | null;
    if (!raw) {
      return null;
    }
    let schedule: Schedule;
    try {
      schedule = JSON.parse(raw) as Schedule;
    } catch {
      return null;
    }
    schedule.date = this.selectedDateIso;
    return { schedule };
  }
}
